import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu, Tag, Tooltip } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import { Row } from 'react-display-flex'

import { emptyCharacter } from '../../../components/EmptyChar'
import { renderDateColumn, renderPrincipalColumn } from '../../../components/tables'
import { renderBasisPointFee, renderCoupon, renderEmail } from '../../../components/tables/table-column-renderers'
import {
  buildSearchFilterProps,
  sortDateColumn,
  sortNumericColumn,
  sortStringColumn,
} from '../../../components/tables/utils'
import { InstrumentCode } from '../../../models'
import { getEnvironmentHost } from '../../../util/environment'
import { IssuerCode, Rfq, RfqQuote } from '../models'

const colors = {
  blue: 'blue',
  green: 'green',
  red: 'red',
  default: '',
  orange: 'orange',
}

const statusColors = {
  open: colors.green,
  closed: colors.blue,
  cancelled: colors.red,
}

const renderTenantLink = (tenantName: string, rfq: Rfq) => {
  const rfqQuotesUrl = `${getEnvironmentHost({ domain: rfq.domain })}/holdings/quotes/${rfq.uuid}`

  return (
    <>
      <Tooltip title={`UUID: ${rfq.uuid}`}>
        <Button type="link" href={rfqQuotesUrl} target="_blank" style={{ paddingLeft: 0 }}>
          {tenantName}
        </Button>
      </Tooltip>
      {rfq.isTrial && (
        <Tag className="item-tag" color="purple">
          TRIAL
        </Tag>
      )}
    </>
  )
}

const getStateKey = (rfq: Rfq): string => {
  const stateOrder = [
    { key: 'cancelledBy', value: rfq.cancelledBy, label: 'Cancelled' },
    { key: 'confirmedBy', value: rfq.confirmedBy, label: 'Closed' },
    { key: 'acceptedBy', value: rfq.acceptedBy, label: 'Accepted' },
    { key: 'signoffCount', value: rfq.signoffCount, label: `Signed Off (${rfq.signoffCount})` },
    { key: 'nominatedBy', value: rfq.nominatedBy, label: 'Nominated' },
    { key: 'open', value: 'open', label: 'Open' },
  ]

  const state = stateOrder.find((item) => item.value !== null && item.value !== undefined)
  return state ? state.label : 'Open'
}

const renderStatus = (status: string, rfq: Rfq) => (
  <>
    <Tag color={statusColors[status]} className="capitalize">
      {getStateKey(rfq)}
    </Tag>
    {status === 'closed' && !rfq.rfqConfirmed && (
      <Tooltip title="Not confirmed">
        <Tag color={colors.red}>NOT CONFIRMED</Tag>
      </Tooltip>
    )}
  </>
)

const renderIssuers = (issuerCodes: IssuerCode[], rfq: Rfq) =>
  issuerCodes.sort().map((issuerCode) => {
    const quotesFromCurrentIssuer = rfq.quotes.filter((quote) => quote.issuerCode === issuerCode)

    const issuerHasQuoted = quotesFromCurrentIssuer.some((quote) => quote.receivedAt)

    const issuerHasNoInterest = quotesFromCurrentIssuer.every((quote) => quote.receivedAt && quote.bankCoupon === null)

    let color = issuerHasQuoted ? colors.green : colors.default
    color = issuerHasNoInterest ? colors.orange : color

    let message = issuerHasQuoted ? 'Quoted' : 'Not quoted'
    message = issuerHasNoInterest ? 'No interested' : message

    return (
      <Tooltip title={message} key={issuerCode}>
        <Tag color={color}>{issuerCode}</Tag>
      </Tooltip>
    )
  })

const renderWinnerIssuerCode = (winnerIssuer: IssuerCode, rfq: Rfq) =>
  winnerIssuer ? (
    <Row>
      <Tag color={colors.green}>{winnerIssuer}</Tag>
      {!rfq.bestExecution && (
        <Tooltip title="Not best execution">
          <Tag color="orange">NBE</Tag>
        </Tooltip>
      )}
    </Row>
  ) : (
    emptyCharacter
  )

interface ActionsMenu {
  onViewQuotes: (quotes: RfqQuote[], rfqUuid: string) => void
  onRollbackRfq: (rfq: Rfq) => void
}

const renderActionsMenu =
  ({ onViewQuotes, onRollbackRfq }: ActionsMenu) =>
  (_: any, rfq: Rfq) => {
    const menu = (
      <Menu>
        <Menu.Item key="viewQuotes" onClick={() => onViewQuotes(rfq.quotes, rfq.uuid)}>
          View Quotes
        </Menu.Item>
        {rfq.status === 'closed' && (
          <Menu.Item key="rollbackRfq" onClick={() => onRollbackRfq(rfq)}>
            Cancel RFQ and Delete Trade
          </Menu.Item>
        )}
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

interface RfqTableColumns {
  onViewQuotes: (quotes: RfqQuote[], rfqUuid: string) => void
  onRollbackRfq: (rfq: Rfq) => void
  filterMenuItems: {
    winnerIssuerCodes: ColumnFilterItem[]
    tenors: ColumnFilterItem[]
    statuses: ColumnFilterItem[]
    issuerCodes: ColumnFilterItem[]
    instrumentCodes: ColumnFilterItem[]
    segmentCodes: ColumnFilterItem[]
  }
  filterState: any
}

export const buildRfqTableColumns = ({
  onViewQuotes,
  onRollbackRfq,
  filterMenuItems,
  filterState,
}: RfqTableColumns) => [
  {
    title: 'Tenant',
    dataIndex: 'tenantName',
    sorter: sortStringColumn({ dataIndex: 'tenantName' }),
    ...buildSearchFilterProps({ dataIndex: 'tenantName', render: renderTenantLink }),
  },
  {
    title: 'Date',
    dataIndex: 'requestedAt',
    render: renderDateColumn,
    sorter: sortDateColumn({ dataIndex: 'requestedAt' }),
  },
  {
    title: 'Asset',
    dataIndex: 'instrumentCode',
    filters: filterMenuItems.instrumentCodes,
    filteredValue: filterState?.instrumentCode || undefined,
    onFilter: (instrumentCode: InstrumentCode, rfq: Rfq) => rfq.instrumentCode === instrumentCode,
  },
  {
    title: 'Principal ($)',
    dataIndex: 'principal',
    render: renderPrincipalColumn,
    sorter: sortNumericColumn({ dataIndex: 'principal' }),
  },
  {
    title: 'Requested by',
    dataIndex: 'requestedBy',
    sorter: sortStringColumn({ dataIndex: 'requestedBy' }),
    ...buildSearchFilterProps({ dataIndex: 'requestedBy', render: renderEmail }),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: renderStatus,
    filters: filterMenuItems.statuses,
    filteredValue: filterState?.statuses || undefined,
    onFilter: (status: string, rfq: Rfq) => rfq.status.includes(status),
  },
  {
    title: 'Segment',
    dataIndex: 'segmentCode',
    filters: filterMenuItems.segmentCodes,
    filteredValue: filterState?.segmentCodes || undefined,
    onFilter: (segmentCode: string, rfq: Rfq) => rfq.segmentCode === segmentCode,
  },
  {
    title: 'Issuers',
    dataIndex: 'issuerCodes',
    render: renderIssuers,
    filters: filterMenuItems.issuerCodes,
    filteredValue: filterState?.issuerCodes || undefined,
    onFilter: (issuerCode: IssuerCode, rfq: Rfq) => rfq.issuerCodes.includes(issuerCode),
  },
  {
    title: 'Winner',
    dataIndex: 'winningIssuerCode',
    render: renderWinnerIssuerCode,
    filters: filterMenuItems.winnerIssuerCodes,
    filteredValue: filterState?.winningIssuerCode || undefined,
    onFilter: (winningIssuerCode: string, rfq: Rfq) => rfq.winningIssuerCode === winningIssuerCode,
  },
  {
    title: 'Cust. Rate',
    dataIndex: 'coupon',
    render: renderCoupon,
    sorter: sortNumericColumn({ dataIndex: 'coupon' }),
  },
  {
    title: 'Rate Sent',
    dataIndex: 'bankCoupon',
    render: renderCoupon,
    sorter: sortNumericColumn({ dataIndex: 'bankCoupon' }),
  },
  {
    title: 'Tenor',
    dataIndex: 'tenor',
    filters: filterMenuItems.tenors,
    filteredValue: filterState?.tenor || undefined,
    onFilter: (tenor: string, rfq: Rfq) => rfq.winningQuote?.tenor === tenor,
  },
  {
    title: 'Emb.Fee',
    dataIndex: 'basisPointFee',
    render: renderBasisPointFee,
    sorter: sortNumericColumn({ dataIndex: 'basisPointFee' }),
  },
  {
    title: 'Fee ($)',
    dataIndex: 'adiFee',
    render: renderPrincipalColumn,
    sorter: sortNumericColumn({ dataIndex: 'adiFee' }),
  },
  { title: '', render: renderActionsMenu({ onViewQuotes, onRollbackRfq }) },
]
